import { useContext, useState } from "react";
import React from "react";
import { Button, Offcanvas, Accordion } from "react-bootstrap";
import { Col, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Loading from "../Loading";
import LabelValuePair from "../components/LabelValuePair";
import {
  StrategyBadge,
  type ModelAssignmentStrategy,
} from "../components/ModelSourceBadge";
import { formatCurrency } from "../lib/numbers";
import { useQueryModel } from "../models/lib";
import { RebalanceContext } from "./RebalanceInfo";
import { useAuthenticatedFetch } from "../lib/api";
import type { UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import type { RebalanceType } from "../../../api/src/rebalances/rebalances.service";
import RebalanceAccountInsights, {
  RebalanceAccountWithMetrics,
} from "./RebalanceAccountInsights";
import RebalanceAccountInsightsDataOnly from "./RebalanceAccountInsightsDataOnly";
import { hasRebalanceMetrics } from "./nullRebalanceMetrics";
import type { AssetAllocationSeries } from "../components/AssetAllocationBarChart";
import {
  createSharedRebalanceData,
  SharedRebalanceData,
  transformV2Model,
} from "./lib/createSharedData";
import RebalanceCheckPanel from "./RebalanceCheckPanel";
import type {
  V2AllocationDetail,
  V2AllocationTreeNode,
  V2CompletionAccountDetail,
  V2CompletionResidualModel,
  V2LossHarvestModification,
  V2SecurityMetadata,
} from "./lib/explainV2Types";
import { rebalanceTypeDisplay } from "./lib";
import CompletionStrategyExplain, {
  CompletionAccountSection,
} from "./CompletionStrategyExplain";
import SoftCompletionExplain, {
  SoftCompletionAccountSection,
} from "./SoftCompletionExplain";

interface AccountSettings {
  account_type: string;
  tax_type: "tax_exempt" | "taxable";
  gains_budget_lt: number | null;
  gains_budget_st: number | null;
  wash_sale_treatment: "avoid_wash_sales";
  lot_selection_algorithm?: "HIFO" | "MTAX";
}

interface Account {
  account_id: number;
  bridge_account_id: number;
  account_name: string;
  rebalance_type: "tolerance";
  is_rebalance_account: boolean;
  raise_spend_cash_amount: number | null;
  settings: AccountSettings;
  model_id: number;
  model: {
    primary: Record<string, number>;
    secondary: Record<string, Record<string, Record<string, number>>>;
  } | null;
  security_restrictions: Record<string, unknown>;
  cash_settings: {
    target_cash_type: "absolute";
    target_cash_weight: number | null;
    target_cash_dollar: number | null;
    minimum_cash_trigger_weight: number | null;
    minimum_cash_trigger_dollar: number | null;
    minimum_cash_trigger_condition: "AND";
    maximum_cash_trigger_weight: number | null;
    maximum_cash_trigger_dollar: number | null;
    maximum_cash_trigger_condition: "AND";
  };
  balance: number | null;
  cash_balance: number | null;
  holdings: null;
}

interface Model {
  primary: Record<string, number>;
  secondary: Record<string, Record<string, Record<string, number>>>;
}

interface ToleranceExplains {
  initial_allocation: Record<string, number>;
  trade_size_adjustment: Record<string, number>;
}

interface SpendCashExplains {
  initial_allocation: Record<string, number>;
  trade_size_adjustment: Record<string, number>;
}

interface RaiseCashExplains {
  initial_allocation: Record<string, number>;
  second_allocation: Record<string, number>;
  trade_size_adjustment: Record<string, number>;
  final_allocation: Record<string, number>;
}

interface AccountLinkProps {
  accountId: string | number;
  accountName: string | null;
}

const AccountLink: React.FC<AccountLinkProps> = ({
  accountId,
  accountName,
}) => {
  if (!accountId) return null;

  return (
    <>
      <a
        href={`/clients/accounts/${accountId}/overview`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {accountName}
      </a>
    </>
  );
};

interface HoldingDict {
  [symbol: string]: {
    shares: number;
    value: number;
    is_bond: boolean;
    price: number;
    current_price?: number;
    weight: number;
    weight_ex_cash: number | null;
    lots: {
      security_value: number;
      lots: any[];
    };
  };
}

const PortfolioCol = ({
  title,
  accountId,
  accountName,
  data,
}: {
  title: string;
  accountId: string;
  accountName: string;
  data: Record<string, number> | null;
}) => (
  <Col md={6}>
    <div>
      <h5>{title}</h5>
      <Table>
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Target Allocation</th>
          </tr>
        </thead>
        <tbody>
          {data &&
            Object.entries(data).map(([symbol, allocation]) => (
              <tr key={symbol}>
                <td>{symbol}</td>
                <td>{allocation}</td>
              </tr>
            ))}
          <tr className="fw-bold">
            <td>Total</td>
            <td>
              {data &&
                Object.values(data).reduce(
                  (sum, allocation) => sum + allocation,
                  0,
                )}
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  </Col>
);

// Helper function to extract manual rebalance data from raw response
const extractManualRebalanceData = (
  accountId: string,
  rawData: any,
  sharedData?: SharedRebalanceData,
  rebalanceType?: RebalanceType,
) => {
  const accountData = rawData[accountId];
  if (!accountData) return null;

  // Get account info from sharedData if available
  const accountInfo = sharedData?.accounts?.[accountId];

  // Calculate totals
  const buyTotal = parseFloat(accountData.buy_total || "0");
  const sellTotal = parseFloat(accountData.sell_total || "0");
  const totalBalance = parseFloat(accountData.balance || "0");

  // Calculate realized gains from trades
  let realizedGainLoss = 0;
  if (accountData.trades) {
    realizedGainLoss = Object.values(accountData.trades).reduce(
      (sum: number, trade: any) => {
        const gainLt = parseFloat(trade.realized_gain_loss_lt || "0");
        const gainSt = parseFloat(trade.realized_gain_loss_st || "0");
        return sum + gainLt + gainSt;
      },
      0,
    );
  }

  // Extract cash balance from result_portfolio
  const cashBalance = accountData.result_portfolio?.["CASH-TRADE"]
    ? parseFloat(
        accountData.result_portfolio["CASH-TRADE"].current_value ||
          accountData.result_portfolio["CASH-TRADE"].value ||
          "0",
      )
    : 0;

  // Use asset allocation data from API response if available
  const assetAllocationFromApi = accountData.assetAllocation || {
    current: [],
    target: [],
    postRebalance: [],
  };

  // Parse risk allocation values to ensure they're numbers (API returns strings)
  const parseRiskAlloc = (alloc: any) => ({
    growth: parseFloat(alloc?.growth || "0") || 0,
    defensive: parseFloat(alloc?.defensive || "0") || 0,
    unknown: parseFloat(alloc?.unknown || "0") || 0,
  });

  const riskAllocation = accountData.riskAllocation
    ? {
        current: parseRiskAlloc(accountData.riskAllocation.current),
        target: parseRiskAlloc(accountData.riskAllocation.target),
        postRebalance: parseRiskAlloc(accountData.riskAllocation.postRebalance),
      }
    : {
        current: {},
        target: {},
        postRebalance: {},
      };

  // Format account type for display (capitalize first letter)
  const formatAccountType = (type: string | undefined) => {
    if (!type) return "Manual Rebalance";
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  };

  return {
    accountId,
    accountName: accountInfo?.account_name || `Account ${accountId}`,
    accountType: formatAccountType(accountInfo?.settings?.account_type),
    modelId: accountInfo?.model_id,
    modelName: accountInfo?.model_name,
    totalBalance,
    cashBalance,
    buyTotal,
    sellTotal: Math.abs(sellTotal), // Make positive for display
    realizedGainLoss,
    trades: accountData.trades || {},
    riskAllocation,
    assetAllocation: assetAllocationFromApi,
    rebalanceType: rebalanceType || ("tolerance" as const),
    raiseSpendCashAmount: accountInfo?.raise_spend_cash_amount ?? undefined,
  };
};

interface TargetRiskAllocationRowProps {
  explainData: {
    account_target_risk_allocations: Record<string, Record<string, number>>;
    security_risk_allocations_by_account: Record<
      string,
      Record<string, Record<string, number | boolean>>
    >;
    allocations?: Record<
      string,
      {
        balance: number;
        composite_risk: number;
        risk_ratio: number;
        growth_dollar: number;
        unallocated_growth_dollar: number;
        growth_weight: number;
      }
    >;
  };
  sharedData: SharedRebalanceData;
}

interface AllocationTableProps {
  allocations: Record<
    string,
    {
      balance: number;
      composite_risk: number;
      risk_ratio: number;
      growth_dollar: number;
      unallocated_growth_dollar: number;
      growth_weight: number;
    }
  >;
  sharedData: SharedRebalanceData;
}

export const AllocationTable = ({
  allocations,
  sharedData,
}: AllocationTableProps) => (
  <>
    <Row>
      <h4>Account Allocations</h4>
    </Row>
    <Row>
      <Col>
        <Table striped bordered>
          <thead>
            <tr>
              <th>Account</th>
              <th>Balance ($)</th>
              <th>Composite Risk ($)</th>
              <th>Risk Ratio</th>
              <th>Growth ($)</th>
              <th>Unallocated Growth ($)</th>
              <th>Growth Weight</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(allocations).map(([accountId, allocation]) => {
              const accountName =
                accountId === "total"
                  ? "Total"
                  : (sharedData.accounts[accountId].account_name ?? accountId);

              return (
                <tr key={accountId}>
                  <td>
                    {accountId === "total" ? (
                      "Total"
                    ) : (
                      <AccountLink
                        accountId={accountId}
                        accountName={accountName}
                      />
                    )}
                  </td>
                  <td>${allocation.balance.toLocaleString()}</td>
                  <td>${allocation.composite_risk.toLocaleString()}</td>
                  <td>{allocation.risk_ratio}</td>
                  <td>${allocation.growth_dollar.toLocaleString()}</td>
                  <td>
                    ${allocation.unallocated_growth_dollar.toLocaleString()}
                  </td>
                  <td>{allocation.growth_weight}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Col>
    </Row>
  </>
);

export const TargetRiskAllocationRow = ({
  explainData,
  sharedData,
}: TargetRiskAllocationRowProps) => (
  <>
    {Object.entries(explainData.account_target_risk_allocations).map(
      ([accountId, allocations]) => {
        const accountName = sharedData.accounts[accountId].account_name;
        return (
          <div key={accountId}>
            <Row>
              <h4>
                {accountId && (
                  <AccountLink
                    accountId={accountId}
                    accountName={accountName}
                  />
                )}
              </h4>
            </Row>
            <Row>
              <Col md={4}>
                <h5>Account Risk Allocations</h5>
                <Table>
                  <thead>
                    <tr>
                      <th>Risk Category</th>
                      <th>Allocation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(allocations).map(
                      ([category, allocation]) => (
                        <tr key={category}>
                          <td>{category}</td>
                          <td>{allocation}</td>
                        </tr>
                      ),
                    )}

                    <tr>
                      <td>Total</td>
                      <td>
                        {Object.values(allocations).reduce(
                          (sum, allocation) => sum + allocation,
                          0,
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
              <Col md={8}>
                <h5>Security Risk Allocations</h5>
                <Table>
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Growth</th>
                      <th>Defensive</th>
                      <th>Cash</th>
                      <th>Unknown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(
                      explainData.security_risk_allocations_by_account[
                        accountId
                      ],
                    ).map(([symbol, risks]) => (
                      <tr key={symbol}>
                        <td>{symbol}</td>
                        <td>{(risks.Growth as number) || 0}</td>
                        <td>{(risks.Defensive as number) || 0}</td>
                        <td>{(risks.Cash as number) || 0}</td>
                        <td>{(risks.Unknown as number) || 0}</td>
                      </tr>
                    ))}

                    <tr>
                      <td>Total</td>
                      <td>
                        {Object.values(
                          explainData.security_risk_allocations_by_account[
                            accountId
                          ],
                        ).reduce(
                          (sum, risks) => sum + ((risks.Growth as number) || 0),
                          0,
                        )}
                      </td>
                      <td>
                        {Object.values(
                          explainData.security_risk_allocations_by_account[
                            accountId
                          ],
                        ).reduce(
                          (sum, risks) =>
                            sum + ((risks.Defensive as number) || 0),
                          0,
                        )}
                      </td>
                      <td>
                        {Object.values(
                          explainData.security_risk_allocations_by_account[
                            accountId
                          ],
                        ).reduce(
                          (sum, risks) => sum + ((risks.Cash as number) || 0),
                          0,
                        )}
                      </td>
                      <td>
                        {Object.values(
                          explainData.security_risk_allocations_by_account[
                            accountId
                          ],
                        ).reduce(
                          (sum, risks) =>
                            sum + ((risks.Unknown as number) || 0),
                          0,
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          </div>
        );
      },
    )}

    {explainData.allocations && (
      <AllocationTable
        allocations={explainData.allocations}
        sharedData={sharedData}
      />
    )}
  </>
);

interface RebalanceRequestExplainStepProps {
  explainData: {
    as_of_date: string;
    household_model: {
      primary: Record<string, number>;
      secondary: Record<string, Record<string, Record<string, unknown>>>;
    };
    accounts: Record<string, Account>;
    category_mapping?: Record<string, Record<string, number>>;
  };
  rebalanceReason?: string;
  rebalanceHint?: string;
  allocationDictionary?: Record<string, number>;
  triggerAllocationDetails?: Record<string, V2AllocationDetail>;
  rebalanceType?: RebalanceType;
  householdModelId?: number | null;
  modelAssignmentStrategy?: ModelAssignmentStrategy | null;
  householdBalance?: number;
}

export const RebalanceRequestExplainStep = ({
  explainData,
  rebalanceReason,
  rebalanceHint,
  allocationDictionary,
  triggerAllocationDetails,
  rebalanceType,
  householdModelId,
  modelAssignmentStrategy,
  householdBalance,
}: RebalanceRequestExplainStepProps) => {
  const {
    isLoading: isLoadingModel,
    isError: isErrorModel,
    data: dataModel,
  } = useQueryModel(householdModelId ?? -1, {
    enabled: householdModelId != null,
  });

  return (
    <div>
      {explainData.as_of_date && (
        <Row className="mb-3">
          <Col className="text-end">
            Rebalance Date:{" "}
            {(() => {
              const [year, month, day] = explainData.as_of_date.split("-");
              return `${parseInt(month)}/${parseInt(day)}/${year}`;
            })()}
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col md={6}>
          <h4>Household</h4>
          {rebalanceType && (
            <LabelValuePair
              label="Rebalance Reason"
              value={rebalanceTypeDisplay[rebalanceType]}
              orientation="horizontal"
              widths={{ label: { xl: 4, lg: 3 } }}
              plaintext
            />
          )}
          <LabelValuePair
            label="Current Model"
            value={
              isLoadingModel ? (
                <Loading />
              ) : isErrorModel ? (
                <span className="text-danger">Failed to load model</span>
              ) : typeof dataModel === "undefined" ? (
                "N/A"
              ) : (
                <>
                  <Link to={`/models/models/${dataModel.id}`}>
                    {dataModel.name}
                  </Link>
                  {modelAssignmentStrategy && (
                    <span className="ms-2">
                      <StrategyBadge strategy={modelAssignmentStrategy} />
                    </span>
                  )}
                </>
              )
            }
            orientation="horizontal"
            widths={{ label: { xl: 4, lg: 3 } }}
            plaintext
          />
          {typeof householdBalance !== "undefined" &&
            householdBalance !== null && (
              <LabelValuePair
                label="AUM"
                value={formatCurrency(householdBalance, 2)}
                orientation="horizontal"
                widths={{ label: { xl: 4, lg: 3 } }}
                plaintext
              />
            )}
        </Col>

        <Col md={6}>
          {(rebalanceReason || rebalanceHint) && (
            <>
              <h4>
                Trigger{" "}
                {rebalanceReason && (
                  <span className="badge bg-secondary ms-2">
                    {rebalanceReason}
                  </span>
                )}
              </h4>
              {rebalanceHint && <div className="mb-3">{rebalanceHint}</div>}
              {triggerAllocationDetails &&
                Object.keys(triggerAllocationDetails).length > 0 && (
                  <Table striped bordered style={{ width: "auto" }}>
                    <thead>
                      <tr>
                        <th>Allocation</th>
                        <th>Current</th>
                        <th>Target</th>
                        <th>Difference</th>
                        <th>Exceeded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(triggerAllocationDetails).map(
                        ([key, detail]) => (
                          <tr key={key}>
                            <td>{detail.name || key}</td>
                            <td>
                              {(
                                parseFloat(detail.current_weight) * 100
                              ).toFixed(1)}
                              %
                            </td>
                            <td>
                              {(parseFloat(detail.target_weight) * 100).toFixed(
                                1,
                              )}
                              %
                            </td>
                            <td>
                              {(parseFloat(detail.difference) * 100).toFixed(1)}
                              %
                            </td>
                            <td className="text-center">
                              {detail.exceeds_threshold ? "✓" : ""}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                )}
              {!triggerAllocationDetails &&
                allocationDictionary &&
                Object.keys(allocationDictionary).length > 0 && (
                  <Table striped bordered style={{ width: "auto" }}>
                    <thead>
                      <tr>
                        <th>Risk Category</th>
                        <th>Allocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(allocationDictionary).map(
                        ([category, allocation]) => (
                          <tr key={category}>
                            <td>{category}</td>
                            <td>{(allocation * 100).toFixed(1)}%</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                )}
            </>
          )}
        </Col>
      </Row>

      <AccountDetailsDrawer
        accounts={explainData.accounts}
        householdModel={explainData.household_model}
        categoryMapping={explainData.category_mapping}
      />
    </div>
  );
};

interface TargetFinalExplainStepProps {
  explainData: {
    account_final_target_portfolios: Record<string, Record<string, number>>;
  };
  sharedData: SharedRebalanceData;
}

export const TargetFinalExplainStep = ({
  explainData,
  sharedData,
}: TargetFinalExplainStepProps) => (
  <>
    <Row>
      <h3>Target - Final</h3>
    </Row>

    {Object.entries(explainData.account_final_target_portfolios).reduce(
      (rows, [account_id, securities], index, array) => {
        if (index % 2 === 0) {
          rows.push(
            <Row>
              <PortfolioCol
                title={`Target Portfolio - ${sharedData.accounts[account_id].account_name}`}
                accountId={account_id}
                accountName={
                  sharedData.accounts[account_id]?.settings.account_type ?? ""
                }
                data={securities}
              />
              {array[index + 1] && (
                <PortfolioCol
                  title={`Target Portfolio - ${sharedData.accounts[array[index + 1][0]]?.account_name}`}
                  accountId={array[index + 1][0]}
                  accountName={
                    sharedData.accounts[array[index + 1][0]]?.settings
                      .account_type ?? ""
                  }
                  data={array[index + 1][1]}
                />
              )}
            </Row>,
          );
        }
        return rows;
      },
      [] as JSX.Element[],
    )}
  </>
);

/**
 * MissingStep Component
 *
 * Displays a debug view when an unrecognized stepName is encountered in the rebalance explain data.
 * This component serves as a fallback to show the raw data structure for steps that haven't been
 * implemented yet, making it easier to develop new step renderers.
 *
 * @param stepName - The name of the unrecognized step from the API response
 * @param explainData - The raw data associated with this step, displayed as formatted JSON
 */
const MissingStep = ({
  stepName,
  explainData,
}: {
  stepName: string;
  explainData: unknown;
}) => (
  <>
    <Row>
      <h3>Unknown Step: {stepName}</h3>
    </Row>
    <div>
      <Row>
        <Col>
          <div>Step type '{stepName}' is not implemented yet</div>
          <pre>{JSON.stringify(explainData, null, 2)}</pre>
        </Col>
      </Row>
    </div>
  </>
);

interface FilterableAttributesStepProps {
  explainData: {
    trade_shares_total: number;
    trade_amount_total: number;
    rebalance_reason: string;
    reason_change: number;
    num_trades: number;
    num_accounts: number;
    realized_gain_loss_total: number;
    buy_total: number;
    sell_total: number;
    unsettled_cash_total: number;
    aum_total: number;
    household_name: string;
    status: string;
  };
  taxLossHarvestedTotal?: number;
}

export const FilterableAttributesStep = ({
  explainData,
  taxLossHarvestedTotal,
}: FilterableAttributesStepProps) => (
  <>
    <Row>
      <h3>Filterable Attributes</h3>
    </Row>
    <div>
      <Row>
        <Col>
          <Table striped bordered>
            <tbody>
              <tr>
                <td>
                  <strong>Household Name</strong>
                </td>
                <td>{explainData.household_name}</td>
              </tr>
              <tr>
                <td>
                  <strong>Status</strong>
                </td>
                <td>{explainData.status}</td>
              </tr>
              <tr>
                <td>
                  <strong>Rebalance Reason</strong>
                </td>
                <td>{explainData.rebalance_reason}</td>
              </tr>
              <tr>
                <td>
                  <strong>Number of Accounts</strong>
                </td>
                <td>{explainData.num_accounts}</td>
              </tr>
              <tr>
                <td>
                  <strong>Number of Trades</strong>
                </td>
                <td>{explainData.num_trades}</td>
              </tr>
              <tr>
                <td>
                  <strong>AUM Total</strong>
                </td>
                <td>${explainData.aum_total.toLocaleString()}</td>
              </tr>
              <tr>
                <td>
                  <strong>Trade Shares Total</strong>
                </td>
                <td>{explainData.trade_shares_total.toLocaleString()}</td>
              </tr>
              <tr>
                <td>
                  <strong>Trade Amount Total</strong>
                </td>
                <td>${explainData.trade_amount_total.toLocaleString()}</td>
              </tr>
              <tr>
                <td>
                  <strong>Buy Total</strong>
                </td>
                <td>${explainData.buy_total.toLocaleString()}</td>
              </tr>
              <tr>
                <td>
                  <strong>Sell Total</strong>
                </td>
                <td>${explainData.sell_total.toLocaleString()}</td>
              </tr>
              <tr>
                <td>
                  <strong>Realized Gain/Loss Total</strong>
                </td>
                <td>
                  ${explainData.realized_gain_loss_total.toLocaleString()}
                </td>
              </tr>
              {taxLossHarvestedTotal ? (
                <tr>
                  <td>
                    <strong>Tax-Loss Harvested</strong>
                  </td>
                  <td>${taxLossHarvestedTotal.toLocaleString()}</td>
                </tr>
              ) : null}
              <tr>
                <td>
                  <strong>Unsettled Cash Total</strong>
                </td>
                <td>${explainData.unsettled_cash_total.toLocaleString()}</td>
              </tr>
              <tr>
                <td>
                  <strong>Reason Change</strong>
                </td>
                <td>{explainData.reason_change}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  </>
);

interface ConstrainedAccountsStepProps {
  explainData: Record<
    string,
    {
      constrained_target: Record<string, number>;
      securities_map: Record<
        string,
        {
          target: number;
          constrained: number;
          risk: string;
          category: string;
          adjusted_target?: number;
          adjusted_allocation?: number;
        }
      >;
      tolerance_explains?: ToleranceExplains;
      spend_cash_explains?: SpendCashExplains;
      raise_cash_explains?: RaiseCashExplains;
    }
  >;
  sharedData: SharedRebalanceData;
}

export const ConstrainedAccountsStep = ({
  explainData,
  sharedData,
}: ConstrainedAccountsStepProps) => {
  const copySecurityMapData = () => {
    const securityMapData = Object.fromEntries(
      Object.entries(explainData).map(([accountId, accountData]) => [
        accountId,
        accountData.securities_map,
      ]),
    );

    navigator.clipboard
      .writeText(JSON.stringify(securityMapData, null, 2))
      .then(() => {
        console.log("Security map data copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy to clipboard:", err);
      });
  };

  return (
    <>
      <Row>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h3>Constrained Target</h3>
          <button
            onClick={copySecurityMapData}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              color: "#6c757d",
            }}
            title="Copy security map data to clipboard"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>
        <p>
          Detailed view of the adjustments made including restrictions and gains
          constrains
        </p>
      </Row>

      <div>
        {Object.entries(explainData).map(([accountId, accountData]) => {
          const accountName =
            sharedData.accounts[accountId]?.account_name || accountId;

          const copyAccountSecurityMapData = () => {
            navigator.clipboard
              .writeText(JSON.stringify(accountData.securities_map, null, 2))
              .then(() => {
                console.log(
                  `Security map data for account ${accountId} copied to clipboard`,
                );
              })
              .catch((err) => {
                console.error("Failed to copy to clipboard:", err);
              });
          };

          return (
            <div key={accountId}>
              <Row>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <h4>
                    <AccountLink
                      accountId={accountId}
                      accountName={accountName}
                    />
                  </h4>
                  <button
                    onClick={copyAccountSecurityMapData}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      color: "#6c757d",
                    }}
                    title={`Copy security map data for ${accountName} to clipboard`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="9"
                        y="9"
                        width="13"
                        height="13"
                        rx="2"
                        ry="2"
                      ></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
              </Row>

              <Row className="mb-4">
                <Col md={12}>
                  <Table striped bordered>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Symbol</th>
                        <th style={{ textAlign: "right" }}>Constrained ($)</th>
                        <th style={{ textAlign: "right" }}>Target ($)</th>
                        {accountData.tolerance_explains && (
                          <>
                            <th style={{ textAlign: "right" }}>
                              Initial Allocation ($)
                            </th>
                            <th style={{ textAlign: "right" }}>
                              Trade Size Adjustment ($)
                            </th>
                          </>
                        )}
                        {accountData.spend_cash_explains && (
                          <>
                            <th style={{ textAlign: "right" }}>
                              Initial Allocation ($)
                            </th>
                            <th style={{ textAlign: "right" }}>
                              Trade Size Adjustment ($)
                            </th>
                          </>
                        )}
                        {accountData.raise_cash_explains && (
                          <>
                            <th style={{ textAlign: "right" }}>
                              Initial Allocation ($)
                            </th>
                            <th style={{ textAlign: "right" }}>
                              Second Allocation ($)
                            </th>
                            <th style={{ textAlign: "right" }}>
                              Trade Size Adjustment ($)
                            </th>
                            <th style={{ textAlign: "right" }}>
                              Final Allocation ($)
                            </th>
                          </>
                        )}
                        <th style={{ textAlign: "right" }}>
                          Adjusted Allocation ($)
                        </th>
                        <th>Target Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(accountData.securities_map)
                        .sort(([symbolA, securityA], [symbolB, securityB]) => {
                          // First sort by Category
                          if (securityA.category < securityB.category)
                            return -1;
                          if (securityA.category > securityB.category) return 1;

                          // If Category is the same, sort by Symbol
                          if (symbolA < symbolB) return -1;
                          if (symbolA > symbolB) return 1;

                          return 0;
                        })
                        .map(([symbol, security]) => (
                          <tr key={symbol}>
                            <td>{security.category}</td>
                            <td>{symbol}</td>
                            <td style={{ textAlign: "right" }}>
                              ${security.constrained.toLocaleString()}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              ${security.target.toLocaleString()}
                            </td>
                            {accountData.tolerance_explains && (
                              <>
                                <td style={{ textAlign: "right" }}>
                                  $
                                  {accountData.tolerance_explains.initial_allocation?.[
                                    symbol
                                  ]?.toLocaleString() || "—"}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  $
                                  {accountData.tolerance_explains.trade_size_adjustment?.[
                                    symbol
                                  ]?.toLocaleString() || "—"}
                                </td>
                              </>
                            )}
                            {accountData.spend_cash_explains && (
                              <>
                                <td style={{ textAlign: "right" }}>
                                  $
                                  {accountData.spend_cash_explains.initial_allocation?.[
                                    symbol
                                  ]?.toLocaleString() || "—"}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  $
                                  {accountData.spend_cash_explains.trade_size_adjustment?.[
                                    symbol
                                  ]?.toLocaleString() || "—"}
                                </td>
                              </>
                            )}
                            {accountData.raise_cash_explains && (
                              <>
                                <td style={{ textAlign: "right" }}>
                                  $
                                  {accountData.raise_cash_explains.initial_allocation?.[
                                    symbol
                                  ]?.toLocaleString() || "—"}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  $
                                  {accountData.raise_cash_explains.second_allocation?.[
                                    symbol
                                  ]?.toLocaleString() || "—"}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  $
                                  {accountData.raise_cash_explains.trade_size_adjustment?.[
                                    symbol
                                  ]?.toLocaleString() || "—"}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  $
                                  {accountData.raise_cash_explains.final_allocation?.[
                                    symbol
                                  ]?.toLocaleString() || "—"}
                                </td>
                              </>
                            )}
                            <td style={{ textAlign: "right" }}>
                              $
                              {security.adjusted_allocation?.toLocaleString() ||
                                "—"}
                            </td>
                            <td>
                              {accountData.constrained_target[symbol] !==
                              undefined
                                ? accountData.constrained_target[symbol]
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      <tr className="fw-bold">
                        <td>—</td>
                        <td>Total</td>
                        <td style={{ textAlign: "right" }}>
                          $
                          {Object.values(accountData.securities_map)
                            .reduce(
                              (sum, security) => sum + security.constrained,
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          $
                          {Object.values(accountData.securities_map)
                            .reduce((sum, security) => sum + security.target, 0)
                            .toLocaleString()}
                        </td>
                        {accountData.tolerance_explains && (
                          <>
                            <td style={{ textAlign: "right" }}>
                              $
                              {Object.values(
                                accountData.tolerance_explains
                                  .initial_allocation || {},
                              )
                                .reduce((sum, value) => sum + (value || 0), 0)
                                .toLocaleString()}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              $
                              {Object.values(
                                accountData.tolerance_explains
                                  .trade_size_adjustment || {},
                              )
                                .reduce((sum, value) => sum + (value || 0), 0)
                                .toLocaleString()}
                            </td>
                          </>
                        )}
                        {accountData.spend_cash_explains && (
                          <>
                            <td style={{ textAlign: "right" }}>
                              $
                              {Object.values(
                                accountData.spend_cash_explains
                                  .initial_allocation || {},
                              )
                                .reduce((sum, value) => sum + (value || 0), 0)
                                .toLocaleString()}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              $
                              {Object.values(
                                accountData.spend_cash_explains
                                  .trade_size_adjustment || {},
                              )
                                .reduce((sum, value) => sum + (value || 0), 0)
                                .toLocaleString()}
                            </td>
                          </>
                        )}
                        {accountData.raise_cash_explains && (
                          <>
                            <td style={{ textAlign: "right" }}>
                              $
                              {Object.values(
                                accountData.raise_cash_explains
                                  .initial_allocation || {},
                              )
                                .reduce((sum, value) => sum + (value || 0), 0)
                                .toLocaleString()}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              $
                              {Object.values(
                                accountData.raise_cash_explains
                                  .second_allocation || {},
                              )
                                .reduce((sum, value) => sum + (value || 0), 0)
                                .toLocaleString()}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              $
                              {Object.values(
                                accountData.raise_cash_explains
                                  .trade_size_adjustment || {},
                              )
                                .reduce((sum, value) => sum + (value || 0), 0)
                                .toLocaleString()}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              $
                              {Object.values(
                                accountData.raise_cash_explains
                                  .final_allocation || {},
                              )
                                .reduce((sum, value) => sum + (value || 0), 0)
                                .toLocaleString()}
                            </td>
                          </>
                        )}
                        <td style={{ textAlign: "right" }}>
                          $
                          {Object.values(accountData.securities_map)
                            .reduce(
                              (sum, security) =>
                                sum + (security.adjusted_allocation || 0),
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td>
                          {Object.values(accountData.constrained_target).reduce(
                            (sum, target) => sum + target,
                            0,
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </div>
          );
        })}
      </div>
    </>
  );
};

interface FinalTaxableTargetPortfolioStepProps {
  explainData: {
    final_target_portfolio: Record<
      string,
      {
        balance: number;
        target: Record<string, number>;
        buy_total: number | null;
        sell_total: number | null;
        trades: unknown | null;
        result_portfolio: unknown | null;
      }
    >;
    total_target_portfolio: {
      balance: number;
      target: Record<string, number>;
      buy_total: number | null;
      sell_total: number | null;
      trades: unknown | null;
      result_portfolio: unknown | null;
    };
  };
}

export const FinalTaxableTargetPortfolioStep = ({
  explainData,
}: FinalTaxableTargetPortfolioStepProps) => (
  <>
    <Row>
      <h3>Final Target Portfolio</h3>
    </Row>

    <div>
      <Row className="mb-3">
        <Col>
          <h5>
            Total Portfolio Balance: $
            {explainData.total_target_portfolio.balance.toLocaleString()}
          </h5>
        </Col>
      </Row>

      {Object.entries(explainData.final_target_portfolio).map(
        ([accountId, account]) => (
          <div key={accountId}>
            <Row>
              <h4>Account: {accountId}</h4>
              <h5>Balance: ${account.balance.toLocaleString()}</h5>
            </Row>

            <Row>
              <Col md={6}>
                <h5>Target Allocations</h5>
                <Table>
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Target Weight</th>
                      <th>Target Value(Converted)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(account.target).map(
                      ([symbol, allocation]) => (
                        <tr key={symbol}>
                          <td>{symbol}</td>
                          <td>{allocation}</td>
                          <td>
                            ${(allocation * account.balance).toLocaleString()}
                          </td>
                        </tr>
                      ),
                    )}
                    <tr className="fw-bold">
                      <td>Total</td>
                      <td>
                        {Object.values(account.target).reduce(
                          (sum, allocation) => sum + allocation,
                          0,
                        )}
                      </td>
                      <td>${account.balance.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          </div>
        ),
      )}

      <Row className="mt-4">
        <h4>Total Target Portfolio</h4>
        <Col md={8}>
          <Table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Target Weight (Converted)</th>
                <th>Target Amount ($)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(explainData.total_target_portfolio.target).map(
                ([symbol, amount]) => (
                  <tr key={symbol}>
                    <td>{symbol}</td>
                    <td>
                      {amount / explainData.total_target_portfolio.balance}
                    </td>
                    <td>${amount.toLocaleString()}</td>
                  </tr>
                ),
              )}
              <tr>
                <td>Total</td>
                <td>N/A</td>
                <td>
                  ${explainData.total_target_portfolio.balance.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  </>
);

interface AccountDetailsDrawerProps {
  accounts: Record<string, Account>;
  householdModel: {
    primary: Record<string, number>;
    secondary: Record<string, Record<string, Record<string, unknown>>>;
  } | null;
  categoryMapping?: Record<string, Record<string, number>>;
}

const AccountDetailsDrawer: React.FC<AccountDetailsDrawerProps> = ({
  accounts,
  householdModel,
  categoryMapping,
}) => {
  const [showHouseholdDrawer, setShowHouseholdDrawer] = React.useState(false);
  const [showAccountsList, setShowAccountsList] = React.useState(false);
  const [showAccountDetails, setShowAccountDetails] = React.useState(false);
  const [selectedAccount, setSelectedAccount] = React.useState<Account | null>(
    null,
  );

  const handleCloseHouseholdDrawer = () => setShowHouseholdDrawer(false);
  const handleShowHouseholdDrawer = () => setShowHouseholdDrawer(true);

  const handleCloseAccountsList = () => setShowAccountsList(false);
  const handleShowAccountsList = () => setShowAccountsList(true);

  const handleCloseAccountDetails = () => setShowAccountDetails(false);
  const handleShowAccountDetails = (account: Account) => {
    setSelectedAccount(account);
    setShowAccountDetails(true);
  };

  return (
    <>
      <>
        <Button
          variant="outline-primary"
          size="sm"
          style={{
            position: "fixed",
            right: "10px",
            top: "45%",
            transform: "rotate(90deg)",
            transformOrigin: "right",
            zIndex: 1000,
          }}
          onClick={handleShowHouseholdDrawer}
        >
          Household
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          style={{
            position: "fixed",
            right: "10px",
            top: "55%",
            transform: "rotate(90deg)",
            transformOrigin: "right",
            zIndex: 1000,
          }}
          onClick={handleShowAccountsList}
        >
          Accounts
        </Button>
      </>

      {/* Household drawer */}
      <Offcanvas
        show={showHouseholdDrawer}
        onHide={handleCloseHouseholdDrawer}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Household Model</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {householdModel ? (
            <>
              <h5>Primary Securities</h5>
              <Table striped bordered>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Target Allocation</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(householdModel.primary).map(
                    ([symbol, allocation]) => (
                      <tr key={symbol}>
                        <td>{symbol}</td>
                        <td>{allocation}</td>
                      </tr>
                    ),
                  )}
                  <tr className="fw-bold">
                    <td>Total</td>
                    <td>
                      {Object.values(householdModel.primary).reduce(
                        (sum, allocation) => sum + allocation,
                        0,
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>

              {Object.keys(householdModel.secondary).length > 0 && (
                <>
                  <h5>Security Groups</h5>
                  <Table striped bordered>
                    <thead>
                      <tr>
                        <th>Primary</th>
                        <th>Secondary</th>
                        <th>Treatment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(householdModel.secondary).map(
                        ([symbol, mapping]) => {
                          const [primarySymbol, treatment] =
                            Object.entries(mapping)[0];
                          const treatmentType = Object.keys(treatment)[0];
                          return (
                            <tr key={symbol}>
                              <td>{primarySymbol}</td>
                              <td>{symbol}</td>
                              <td>{treatmentType}</td>
                            </tr>
                          );
                        },
                      )}
                    </tbody>
                  </Table>
                </>
              )}

              {categoryMapping && Object.keys(categoryMapping).length > 0 && (
                <>
                  <h5>Category Mappings</h5>
                  <Table striped bordered>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Growth</th>
                        <th>Defensive</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(categoryMapping).map(
                        ([category, allocations]) => (
                          <tr key={category}>
                            <td>{category}</td>
                            <td>{allocations.growth}</td>
                            <td>{allocations.defensive}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          ) : (
            <p>No household model available</p>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Accounts List drawer */}
      <Offcanvas
        show={showAccountsList}
        onHide={handleCloseAccountsList}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Accounts</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Account Name</th>
                <th>Type</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(accounts).map((account) => (
                <tr
                  key={account.account_id}
                  onClick={() => handleShowAccountDetails(account)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{account.account_name}</td>
                  <td>{account.settings.account_type}</td>
                  <td>${account.balance?.toLocaleString() ?? "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Account Details drawer */}
      <Offcanvas
        show={showAccountDetails}
        onHide={handleCloseAccountDetails}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {selectedAccount && (
              <AccountLink
                accountId={selectedAccount.account_id}
                accountName={selectedAccount.account_name}
              />
            )}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {selectedAccount && (
            <>
              <Table striped bordered>
                <tbody>
                  <tr>
                    <td>Account ID</td>
                    <td>{selectedAccount.account_id}</td>
                  </tr>
                  <tr>
                    <td>Bridge Account ID</td>
                    <td>{selectedAccount.bridge_account_id}</td>
                  </tr>
                  <tr>
                    <td>Account Type</td>
                    <td>{selectedAccount.settings.account_type}</td>
                  </tr>
                  <tr>
                    <td>Tax Type</td>
                    <td>{selectedAccount.settings.tax_type}</td>
                  </tr>
                  <tr>
                    <td>Balance</td>
                    <td>
                      ${selectedAccount.balance?.toLocaleString() ?? "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Est. Unsettled Cash</td>
                    <td>
                      ${selectedAccount.cash_balance?.toLocaleString() ?? "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Rebalance Type</td>
                    <td>{selectedAccount.rebalance_type}</td>
                  </tr>
                  <tr>
                    <td>Is Rebalance Account</td>
                    <td>
                      {selectedAccount.is_rebalance_account ? "Yes" : "No"}
                    </td>
                  </tr>
                  <tr>
                    <td>Capital Gains (Short Term)</td>
                    <td>
                      {selectedAccount.settings.gains_budget_st
                        ? selectedAccount.settings.gains_budget_st
                        : "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Capital Gains (Long Term)</td>
                    <td>
                      {selectedAccount.settings.gains_budget_lt
                        ? selectedAccount.settings.gains_budget_lt
                        : "N/A"}
                    </td>
                  </tr>
                </tbody>
              </Table>

              <h5>Cash Settings</h5>
              <Table striped bordered>
                <tbody>
                  <tr>
                    <td>Target Cash Type</td>
                    <td>{selectedAccount.cash_settings.target_cash_type}</td>
                  </tr>
                  <tr>
                    <td>Target Cash Weight</td>
                    <td>
                      {selectedAccount.cash_settings.target_cash_weight !== null
                        ? `${selectedAccount.cash_settings.target_cash_weight}%`
                        : "—"}
                    </td>
                  </tr>
                  <tr>
                    <td>Target Cash Dollar</td>
                    <td>
                      ${selectedAccount.cash_settings.target_cash_dollar ?? "—"}
                    </td>
                  </tr>
                  <tr>
                    <td>Min Cash Settings</td>
                    <td>
                      {selectedAccount.cash_settings
                        .minimum_cash_trigger_dollar !== null
                        ? `$${selectedAccount.cash_settings.minimum_cash_trigger_dollar}`
                        : "—"}{" "}
                      {
                        selectedAccount.cash_settings
                          .minimum_cash_trigger_condition
                      }{" "}
                      {selectedAccount.cash_settings
                        .minimum_cash_trigger_weight !== null
                        ? `${selectedAccount.cash_settings.minimum_cash_trigger_weight}%`
                        : "—"}
                    </td>
                  </tr>
                  <tr>
                    <td>Max Cash Settings</td>
                    <td>
                      {selectedAccount.cash_settings
                        .maximum_cash_trigger_dollar !== null
                        ? `$${selectedAccount.cash_settings.maximum_cash_trigger_dollar}`
                        : "—"}{" "}
                      {
                        selectedAccount.cash_settings
                          .maximum_cash_trigger_condition
                      }{" "}
                      {selectedAccount.cash_settings
                        .maximum_cash_trigger_weight !== null
                        ? `${selectedAccount.cash_settings.maximum_cash_trigger_weight}%`
                        : "—"}
                    </td>
                  </tr>
                </tbody>
              </Table>

              {selectedAccount.model && (
                <>
                  <h5>Model Portfolio</h5>
                  <Table striped bordered>
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Target Allocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(selectedAccount.model.primary).map(
                        ([symbol, allocation]) => (
                          <tr key={symbol}>
                            <td>{symbol}</td>
                            <td>{allocation}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>

                  {Object.keys(selectedAccount.security_restrictions).length >
                    0 && (
                    <>
                      <h5>Security Restrictions</h5>
                      <Table striped bordered>
                        <thead>
                          <tr>
                            <th>Symbol</th>
                            <th>Treatment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(
                            selectedAccount.security_restrictions,
                          ).map(([symbol, treatment]) => (
                            <tr key={symbol}>
                              <td>{symbol}</td>
                              <td>{String(treatment)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}

                  {Object.keys(selectedAccount.model.secondary).length > 0 && (
                    <>
                      <h5>Secondary Securities</h5>
                      <Table striped bordered>
                        <thead>
                          <tr>
                            <th>Symbol</th>
                            <th>Secondary To</th>
                            <th>Treatment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(selectedAccount.model.secondary).map(
                            ([secondarySymbol, mapping]) => {
                              const [primarySymbol, treatment] =
                                Object.entries(mapping)[0];
                              const treatmentType = Object.keys(treatment)[0];
                              return (
                                <tr key={secondarySymbol}>
                                  <td>{secondarySymbol}</td>
                                  <td>{primarySymbol}</td>
                                  <td>{treatmentType}</td>
                                </tr>
                              );
                            },
                          )}
                        </tbody>
                      </Table>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

// V2 Component to render target allocations from targets step
interface V2AccountTargetTableProps {
  accountTargets: {
    initial_holdings: Record<string, string>;
    model_target: Record<string, string>;
    adjusted_target: Record<string, string>;
    constrained_target: Record<string, string>;
    final_target: Record<string, string>;
  };
  accountBalance: number;
  isTaxable: boolean;
  trades?: Record<
    string,
    {
      shares: number;
      price: number;
      realized_gain_loss_st?: number;
      realized_gain_loss_lt?: number;
      tax_loss_harvest_flag?: boolean;
    }
  >;
}

export const V2AccountTargetTable = ({
  accountTargets,
  accountBalance,
  isTaxable,
  trades,
}: V2AccountTargetTableProps) => {
  // Collect all symbols from all allocation stages
  const allSymbols = new Set<string>([
    ...Object.keys(accountTargets.initial_holdings || {}),
    ...Object.keys(accountTargets.model_target || {}),
    ...Object.keys(accountTargets.adjusted_target || {}),
    ...Object.keys(accountTargets.constrained_target || {}),
    ...Object.keys(accountTargets.final_target || {}),
  ]);

  const sortedSymbols = Array.from(allSymbols).sort();

  // Calculate totals
  const totals = {
    holdingsDollar: 0,
    holdingsWeight: 0,
    adjustedTarget: 0,
    modelTarget: 0,
    constrainedTarget: 0,
    rgl: 0,
    amount: 0,
  };

  sortedSymbols.forEach((symbol) => {
    const initialWeight = parseFloat(
      accountTargets.initial_holdings[symbol] || "0",
    );
    totals.holdingsDollar += initialWeight * accountBalance;
    totals.holdingsWeight += initialWeight;
    totals.adjustedTarget += parseFloat(
      accountTargets.adjusted_target[symbol] || "0",
    );
    totals.modelTarget += parseFloat(
      accountTargets.model_target[symbol] || "0",
    );
    totals.constrainedTarget += parseFloat(
      accountTargets.constrained_target[symbol] || "0",
    );
    if (trades?.[symbol]) {
      totals.rgl +=
        (trades[symbol].realized_gain_loss_st || 0) +
        (trades[symbol].realized_gain_loss_lt || 0);
      totals.amount +=
        (trades[symbol].shares || 0) * (trades[symbol].price || 0);
    }
  });

  return (
    <Table style={{ fontSize: "0.875rem" }}>
      <thead>
        <tr>
          <th style={{ padding: "0.25rem" }}>Symbol</th>
          <th colSpan={2} style={{ textAlign: "center", padding: "0.25rem" }}>
            Holdings
          </th>
          <th style={{ padding: "0.25rem", textAlign: "right" }}>
            Target w. Cash
          </th>
          <th style={{ padding: "0.25rem", textAlign: "right" }}>
            Model Target
          </th>
          <th style={{ padding: "0.25rem", textAlign: "right" }}>
            Constrained Target
          </th>
          <th style={{ padding: "0.25rem", textAlign: "right" }}>
            Change <span style={{ fontSize: "0.8em", color: "#aaa" }}>(Δ)</span>
          </th>
          <th style={{ padding: "0.25rem", textAlign: "right" }}>
            Shares <span style={{ fontSize: "0.8em", color: "#aaa" }}>(Δ)</span>
          </th>
          {isTaxable && (
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              RGL <span style={{ fontSize: "0.8em", color: "#aaa" }}>(Δ)</span>
            </th>
          )}
          <th style={{ padding: "0.25rem", textAlign: "right" }}>
            Amount <span style={{ fontSize: "0.8em", color: "#aaa" }}>(Δ)</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {sortedSymbols.map((symbol) => {
          const initialWeight = parseFloat(
            accountTargets.initial_holdings[symbol] || "0",
          );
          const constrainedWeight = parseFloat(
            accountTargets.constrained_target[symbol] || "0",
          );
          const change = constrainedWeight - initialWeight;
          const trade = trades?.[symbol];

          return (
            <tr key={symbol}>
              <td style={{ padding: "0.25rem" }}>{symbol}</td>
              <td style={{ textAlign: "right", padding: "0.25rem" }}>
                {initialWeight > 0
                  ? `$${(initialWeight * accountBalance).toLocaleString()}`
                  : "—"}
              </td>
              <td style={{ padding: "0.25rem" }}>
                {initialWeight > 0 ? initialWeight.toFixed(6) : "—"}
              </td>
              <td style={{ padding: "0.25rem", textAlign: "right" }}>
                {accountTargets.adjusted_target[symbol]
                  ? parseFloat(accountTargets.adjusted_target[symbol]).toFixed(
                      6,
                    )
                  : "—"}
              </td>
              <td style={{ padding: "0.25rem", textAlign: "right" }}>
                {accountTargets.model_target[symbol]
                  ? parseFloat(accountTargets.model_target[symbol]).toFixed(6)
                  : "—"}
              </td>
              <td style={{ padding: "0.25rem", textAlign: "right" }}>
                {accountTargets.constrained_target[symbol]
                  ? parseFloat(
                      accountTargets.constrained_target[symbol],
                    ).toFixed(6)
                  : "—"}
              </td>
              <td style={{ padding: "0.25rem", textAlign: "right" }}>
                {change.toFixed(4)}
              </td>
              <td style={{ padding: "0.25rem", textAlign: "right" }}>
                {trade?.shares ?? "—"}
              </td>
              {isTaxable && (
                <td style={{ padding: "0.25rem", textAlign: "right" }}>
                  {trade?.realized_gain_loss_lt !== undefined ||
                  trade?.realized_gain_loss_st !== undefined
                    ? `$${((trade.realized_gain_loss_lt || 0) + (trade.realized_gain_loss_st || 0)).toLocaleString()}`
                    : "—"}
                  {trade?.tax_loss_harvest_flag && (
                    <span
                      className="badge bg-success ms-2"
                      title="Sell originated from a tax-loss-harvest target rewrite"
                    >
                      TLH
                    </span>
                  )}
                </td>
              )}
              <td style={{ padding: "0.25rem", textAlign: "right" }}>
                {trade?.shares && trade?.price
                  ? `$${(trade.shares * trade.price).toLocaleString()}`
                  : "—"}
              </td>
            </tr>
          );
        })}

        {/* Totals Row */}
        <tr className="fw-bold">
          <td style={{ padding: "0.25rem" }}>Total</td>
          <td style={{ textAlign: "right", padding: "0.25rem" }}>
            ${totals.holdingsDollar.toLocaleString()}
          </td>
          <td style={{ padding: "0.25rem" }}>
            {totals.holdingsWeight.toFixed(6)}
          </td>
          <td style={{ padding: "0.25rem", textAlign: "right" }}>
            {totals.adjustedTarget.toFixed(6)}
          </td>
          <td style={{ padding: "0.25rem", textAlign: "right" }}>
            {totals.modelTarget.toFixed(6)}
          </td>
          <td style={{ padding: "0.25rem", textAlign: "right" }}>
            {totals.constrainedTarget.toFixed(6)}
          </td>
          <td style={{ padding: "0.25rem", textAlign: "right" }}>—</td>
          <td style={{ padding: "0.25rem", textAlign: "right" }}>—</td>
          {isTaxable && (
            <td style={{ padding: "0.25rem", textAlign: "right" }}>
              {totals.rgl === 0 ? "—" : `$${totals.rgl.toLocaleString()}`}
            </td>
          )}
          <td style={{ padding: "0.25rem", textAlign: "right" }}>
            {totals.amount === 0 ? "—" : `$${totals.amount.toLocaleString()}`}
          </td>
        </tr>
      </tbody>
    </Table>
  );
};

// Component to render account-specific constrained target table
interface AccountConstrainedTableProps {
  accountId: string;
  accountData: {
    securities_map: Record<
      string,
      V2SecurityMetadata & { adjusted_allocation?: number }
    >;
    final_target: Record<string, string>;
  };
  accountName: string;
}

const formatDollar = (val: number) =>
  `$${val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const AccountConstrainedTable = ({
  accountId,
  accountData,
  accountName,
}: AccountConstrainedTableProps) => {
  const copyAccountSecurityMapData = () => {
    navigator.clipboard
      .writeText(JSON.stringify(accountData.securities_map, null, 2))
      .then(() => {
        console.log(
          `Security map data for account ${accountId} copied to clipboard`,
        );
      })
      .catch((err) => {
        console.error("Failed to copy to clipboard:", err);
      });
  };

  const entries = Object.entries(accountData.securities_map).sort(
    ([symbolA, a], [symbolB, b]) => {
      if (a.category < b.category) return -1;
      if (a.category > b.category) return 1;
      if (symbolA < symbolB) return -1;
      if (symbolA > symbolB) return 1;
      return 0;
    },
  );

  const totals = entries.reduce(
    (acc, [, sec]) => ({
      constrained: acc.constrained + sec.constrained,
      target: acc.target + sec.target,
      allocated: acc.allocated + (sec.adjusted_allocation ?? 0),
    }),
    { constrained: 0, target: 0, allocated: 0 },
  );

  const totalWeight = Object.values(accountData.final_target).reduce(
    (sum, w) => sum + parseFloat(w),
    0,
  );

  return (
    <div>
      <Row>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h5>Constrained Target</h5>
          <button
            onClick={copyAccountSecurityMapData}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              color: "#6c757d",
            }}
            title={`Copy security map data for ${accountName} to clipboard`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Table striped bordered size="sm">
            <thead>
              <tr>
                <th>Category</th>
                <th>Symbol</th>
                <th style={{ textAlign: "right" }}>Constrained</th>
                <th style={{ textAlign: "right" }}>Target</th>
                <th style={{ textAlign: "right" }}>Allocated</th>
                <th style={{ textAlign: "right" }}>Target Weight</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([symbol, security]) => (
                <tr key={symbol}>
                  <td>{security.category}</td>
                  <td>{symbol}</td>
                  <td style={{ textAlign: "right" }}>
                    {formatDollar(security.constrained)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatDollar(security.target)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {security.adjusted_allocation != null
                      ? formatDollar(security.adjusted_allocation)
                      : "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {accountData.final_target[symbol] ?? "—"}
                  </td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td colSpan={2}>Total</td>
                <td style={{ textAlign: "right" }}>
                  {formatDollar(totals.constrained)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {formatDollar(totals.target)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {formatDollar(totals.allocated)}
                </td>
                <td style={{ textAlign: "right" }}>{totalWeight.toFixed(4)}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  );
};

// HIFO Lot adjustment type definition
interface HifoLotAdjustment {
  symbol: string;
  primary_symbol: string;
  lot_value: number | string;
  lot_cost_basis: number | string;
  unrealized_gain: number | string;
  term: "L" | "S";
  action: "sold_full" | "sold_partial" | "skipped";
  limiting_factor:
    | "fully_sold"
    | "overweight_resolved"
    | "budget_l"
    | "budget_s"
    | "no_budget"
    | "unknown_term";
  amount_sold: number | string;
  gain_or_loss: number | string;
  budget_lt_before: number | string;
  budget_lt_after: number | string;
  budget_st_before: number | string;
  budget_st_after: number | string;
  reason: string;
}

// MTAX Lot adjustment type definition
interface MtaxLotAdjustment {
  symbol: string;
  primary_symbol: string;
  lot_value: number | string;
  lot_cost_basis: number | string;
  unrealized_gain: number | string;
  term: "L" | "S";
  action: "sold_full" | "sold_partial" | "skipped";
  limiting_factor:
    "fully_sold" | "overweight_resolved" | "budget_exhausted" | "no_budget";
  amount_sold: number | string;
  gain_or_loss: number | string;
  tax_impact: number | string;
  tax_budget_before: number | string;
  tax_budget_after: number | string;
  reason: string;
}

// Union type for lot adjustments
type LotAdjustment = HifoLotAdjustment | MtaxLotAdjustment;

// Helper function to format limiting factor for display
const formatLimitingFactor = (factor: string): string => {
  switch (factor) {
    case "fully_sold":
      return "—";
    case "overweight_resolved":
      return "Target Reached";
    case "budget_l":
      return "LT Budget Limit";
    case "budget_s":
      return "ST Budget Limit";
    case "budget_exhausted":
      return "Budget Exhausted";
    case "no_budget":
      return "No Budget";
    case "unknown_term":
      return "Unknown Term";
    default:
      return factor;
  }
};

// Helper function to format action for display
const formatAction = (action: string): { label: string; color: string } => {
  switch (action) {
    case "sold_full":
      return { label: "Sold Full", color: "#198754" }; // green
    case "sold_partial":
      return { label: "Sold Partial", color: "#ffc107" }; // yellow
    case "skipped":
      return { label: "Skipped", color: "#6c757d" }; // gray
    default:
      return { label: action, color: "#6c757d" };
  }
};

// Helper function to format term for display
const formatTerm = (term: string): string => {
  switch (term) {
    case "L":
      return "LT";
    case "S":
      return "ST";
    default:
      return term;
  }
};

// Helper to parse numeric values that may be strings or undefined
const parseNumericValue = (
  value: number | string | undefined | null,
): number => {
  if (value === undefined || value === null) {
    return 0;
  }
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return value;
};

const fmt2 = (value: number): string =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Component to render HIFO lot adjustments table for taxable accounts
interface HifoLotAdjustmentsTableProps {
  lotAdjustments: HifoLotAdjustment[];
}

const HifoLotAdjustmentsTable = ({
  lotAdjustments,
}: HifoLotAdjustmentsTableProps) => {
  if (!lotAdjustments || lotAdjustments.length === 0) {
    return null;
  }

  // Get budget summary from first and last lot
  const firstLot = lotAdjustments[0];
  const lastLot = lotAdjustments[lotAdjustments.length - 1];

  const startingLtBudget = parseNumericValue(firstLot.budget_lt_before);
  const startingStBudget = parseNumericValue(firstLot.budget_st_before);
  const endingLtBudget = parseNumericValue(lastLot.budget_lt_after);
  const endingStBudget = parseNumericValue(lastLot.budget_st_after);

  return (
    <div className="mt-4">
      <h5>Lot Selection (HIFO)</h5>

      {/* Budget Summary - right aligned small table */}
      <div className="mb-3 d-flex justify-content-end">
        <table style={{ fontSize: "0.875rem", width: "auto" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #dee2e6" }}>
              <th></th>
              <th
                style={{
                  textAlign: "right",
                  padding: "0.25rem 0.5rem",
                  fontWeight: 600,
                }}
              >
                LT
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "0.25rem 0.5rem",
                  fontWeight: 600,
                }}
              >
                ST
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "0.25rem 0.5rem", fontWeight: 600 }}>
                Starting
              </td>
              <td style={{ textAlign: "right", padding: "0.25rem 0.5rem" }}>
                ${fmt2(startingLtBudget)}
              </td>
              <td style={{ textAlign: "right", padding: "0.25rem 0.5rem" }}>
                ${fmt2(startingStBudget)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "0.25rem 0.5rem", fontWeight: 600 }}>
                Ending
              </td>
              <td style={{ textAlign: "right", padding: "0.25rem 0.5rem" }}>
                ${fmt2(endingLtBudget)}
              </td>
              <td style={{ textAlign: "right", padding: "0.25rem 0.5rem" }}>
                ${fmt2(endingStBudget)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Table striped bordered style={{ fontSize: "0.875rem" }}>
        <thead>
          <tr>
            <th style={{ padding: "0.25rem" }}>Symbol</th>
            <th style={{ padding: "0.25rem" }}>Term</th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Lot Value
            </th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Cost Basis
            </th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Gain/Loss
            </th>
            <th style={{ padding: "0.25rem" }}>Action</th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Amount Sold
            </th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>Realized</th>
            <th style={{ padding: "0.25rem" }}>Limiting Factor</th>
          </tr>
        </thead>
        <tbody>
          {lotAdjustments.map((lot, index) => {
            const lotValue = parseNumericValue(lot.lot_value);
            const costBasis = parseNumericValue(lot.lot_cost_basis);
            const unrealizedGain = parseNumericValue(lot.unrealized_gain);
            const amountSold = parseNumericValue(lot.amount_sold);
            const realizedGainLoss = parseNumericValue(lot.gain_or_loss);
            const actionInfo = formatAction(lot.action);

            // Determine row styling based on gain/loss
            const rowStyle: React.CSSProperties = {};
            if (lot.action === "skipped") {
              rowStyle.backgroundColor = "#f8f9fa"; // gray/muted
              rowStyle.color = "#6c757d";
            } else if (unrealizedGain < 0) {
              rowStyle.backgroundColor = "#d1e7dd"; // green tint (loss = tax beneficial)
            } else if (unrealizedGain > 0) {
              rowStyle.backgroundColor = "#fff3cd"; // orange/yellow tint (gain = consumes budget)
            }

            return (
              <tr key={`${lot.symbol}-${index}`} style={rowStyle}>
                <td style={{ padding: "0.25rem" }}>
                  {lot.symbol}
                  {lot.primary_symbol !== lot.symbol && (
                    <small className="text-muted">
                      {" "}
                      ({lot.primary_symbol})
                    </small>
                  )}
                </td>
                <td style={{ padding: "0.25rem" }}>{formatTerm(lot.term)}</td>
                <td style={{ padding: "0.25rem", textAlign: "right" }}>
                  ${fmt2(lotValue)}
                </td>
                <td style={{ padding: "0.25rem", textAlign: "right" }}>
                  ${fmt2(costBasis)}
                </td>
                <td
                  style={{
                    padding: "0.25rem",
                    textAlign: "right",
                    color: unrealizedGain < 0 ? "#198754" : "#dc3545",
                  }}
                >
                  {unrealizedGain < 0 ? "-" : "+"}$
                  {fmt2(Math.abs(unrealizedGain))}
                </td>
                <td style={{ padding: "0.25rem" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      backgroundColor: actionInfo.color,
                      color: lot.action === "sold_partial" ? "#000" : "#fff",
                      fontSize: "0.75rem",
                    }}
                  >
                    {actionInfo.label}
                  </span>
                </td>
                <td style={{ padding: "0.25rem", textAlign: "right" }}>
                  {amountSold > 0 ? `$${fmt2(amountSold)}` : "—"}
                </td>
                <td
                  style={{
                    padding: "0.25rem",
                    textAlign: "right",
                    color: realizedGainLoss < 0 ? "#198754" : "#dc3545",
                  }}
                >
                  {realizedGainLoss !== 0 ? (
                    <>
                      {realizedGainLoss < 0 ? "-" : "+"}$
                      {fmt2(Math.abs(realizedGainLoss))}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td style={{ padding: "0.25rem" }}>
                  {formatLimitingFactor(lot.limiting_factor)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

// Component to render MTAX lot adjustments table for taxable accounts
interface MtaxLotAdjustmentsTableProps {
  lotAdjustments: MtaxLotAdjustment[];
}

const MtaxLotAdjustmentsTable = ({
  lotAdjustments,
}: MtaxLotAdjustmentsTableProps) => {
  if (!lotAdjustments || lotAdjustments.length === 0) {
    return null;
  }

  // Get budget summary from first and last lot
  const firstLot = lotAdjustments[0];
  const lastLot = lotAdjustments[lotAdjustments.length - 1];

  const startingTaxBudget = parseNumericValue(firstLot.tax_budget_before);
  const endingTaxBudget = parseNumericValue(lastLot.tax_budget_after);

  return (
    <div className="mt-4">
      <h5>Lot Selection (MTAX)</h5>

      {/* Budget Summary - right aligned small table */}
      <div className="mb-3 d-flex justify-content-end">
        <table style={{ fontSize: "0.875rem", width: "auto" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #dee2e6" }}>
              <th></th>
              <th
                style={{
                  textAlign: "right",
                  padding: "0.25rem 0.5rem",
                  fontWeight: 600,
                }}
              >
                Tax Budget
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "0.25rem 0.5rem", fontWeight: 600 }}>
                Starting
              </td>
              <td style={{ textAlign: "right", padding: "0.25rem 0.5rem" }}>
                ${fmt2(startingTaxBudget)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "0.25rem 0.5rem", fontWeight: 600 }}>
                Ending
              </td>
              <td style={{ textAlign: "right", padding: "0.25rem 0.5rem" }}>
                ${fmt2(endingTaxBudget)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Table striped bordered style={{ fontSize: "0.875rem" }}>
        <thead>
          <tr>
            <th style={{ padding: "0.25rem" }}>Symbol</th>
            <th style={{ padding: "0.25rem" }}>Term</th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Lot Value
            </th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Unrealized G/L
            </th>
            <th style={{ padding: "0.25rem" }}>Action</th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Amount Sold
            </th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Realized G/L
            </th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Tax Impact
            </th>
            <th style={{ padding: "0.25rem" }}>Limiting Factor</th>
          </tr>
        </thead>
        <tbody>
          {lotAdjustments.map((lot, index) => {
            const lotValue = parseNumericValue(lot.lot_value);
            const unrealizedGain = parseNumericValue(lot.unrealized_gain);
            const amountSold = parseNumericValue(lot.amount_sold);
            const realizedGainLoss = parseNumericValue(lot.gain_or_loss);
            const taxImpact = parseNumericValue(lot.tax_impact);
            const actionInfo = formatAction(lot.action);

            // Determine row styling based on gain/loss
            const rowStyle: React.CSSProperties = {};
            if (lot.action === "skipped") {
              rowStyle.backgroundColor = "#f8f9fa"; // gray/muted
              rowStyle.color = "#6c757d";
            } else if (unrealizedGain < 0) {
              rowStyle.backgroundColor = "#d1e7dd"; // green tint (loss = tax beneficial)
            } else if (unrealizedGain > 0) {
              rowStyle.backgroundColor = "#fff3cd"; // orange/yellow tint (gain = consumes budget)
            }

            return (
              <tr key={`${lot.symbol}-${index}`} style={rowStyle}>
                <td style={{ padding: "0.25rem" }}>
                  {lot.symbol}
                  {lot.primary_symbol !== lot.symbol && (
                    <small className="text-muted">
                      {" "}
                      ({lot.primary_symbol})
                    </small>
                  )}
                </td>
                <td style={{ padding: "0.25rem" }}>{formatTerm(lot.term)}</td>
                <td style={{ padding: "0.25rem", textAlign: "right" }}>
                  ${fmt2(lotValue)}
                </td>
                <td
                  style={{
                    padding: "0.25rem",
                    textAlign: "right",
                    color: unrealizedGain < 0 ? "#198754" : "#dc3545",
                  }}
                >
                  {unrealizedGain < 0 ? "-" : "+"}$
                  {fmt2(Math.abs(unrealizedGain))}
                </td>
                <td style={{ padding: "0.25rem" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      backgroundColor: actionInfo.color,
                      color: lot.action === "sold_partial" ? "#000" : "#fff",
                      fontSize: "0.75rem",
                    }}
                  >
                    {actionInfo.label}
                  </span>
                </td>
                <td style={{ padding: "0.25rem", textAlign: "right" }}>
                  {amountSold > 0 ? `$${fmt2(amountSold)}` : "—"}
                </td>
                <td
                  style={{
                    padding: "0.25rem",
                    textAlign: "right",
                    color: realizedGainLoss < 0 ? "#198754" : "#dc3545",
                  }}
                >
                  {realizedGainLoss !== 0 ? (
                    <>
                      {realizedGainLoss < 0 ? "-" : "+"}$
                      {fmt2(Math.abs(realizedGainLoss))}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td
                  style={{
                    padding: "0.25rem",
                    textAlign: "right",
                    color: taxImpact < 0 ? "#198754" : "#dc3545",
                  }}
                >
                  {taxImpact !== 0 ? (
                    <>
                      {taxImpact < 0 ? "-" : "+"}${fmt2(Math.abs(taxImpact))}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td style={{ padding: "0.25rem" }}>
                  {formatLimitingFactor(lot.limiting_factor)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

// Component to render the Tax-Loss Harvest target modifications for an account.
// APP-1205. The backend lowers the loss-bearing security's target to its
// post-sell holding and shifts the freed weight onto a like_primary alternate;
// each entry here describes one such rewrite.
interface V2LossHarvestSectionProps {
  modifications: V2LossHarvestModification[];
}

export const V2LossHarvestSection = ({
  modifications,
}: V2LossHarvestSectionProps) => {
  if (!modifications || modifications.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <h5>Tax-Loss Harvest</h5>
      <Table striped bordered style={{ fontSize: "0.875rem" }}>
        <thead>
          <tr>
            <th style={{ padding: "0.25rem" }}>Sell</th>
            <th style={{ padding: "0.25rem" }}>Buy</th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Harvestable Loss
            </th>
            <th style={{ padding: "0.25rem", textAlign: "right" }}>
              Target Shift
            </th>
            <th style={{ padding: "0.25rem" }}>Explanation</th>
          </tr>
        </thead>
        <tbody>
          {modifications.map((m, i) => {
            const shift =
              parseFloat(m.primary_target_before) -
              parseFloat(m.primary_target_after);
            return (
              <tr key={`${m.primary_symbol}-${m.alternate_symbol}-${i}`}>
                <td style={{ padding: "0.25rem" }}>{m.primary_symbol}</td>
                <td style={{ padding: "0.25rem" }}>{m.alternate_symbol}</td>
                <td style={{ padding: "0.25rem", textAlign: "right" }}>
                  ${parseFloat(m.harvestable_loss).toLocaleString()}
                </td>
                <td style={{ padding: "0.25rem", textAlign: "right" }}>
                  {shift.toFixed(4)}
                </td>
                <td style={{ padding: "0.25rem" }}>{m.explanation}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

// Component to render all data for a single account grouped together
export interface AccountExplainSectionProps {
  accountId: string;
  v2TargetsData?: {
    initial_holdings: Record<string, string>;
    model_target: Record<string, string>;
    adjusted_target: Record<string, string>;
    constrained_target: Record<string, string>;
    final_target: Record<string, string>;
  };
  constrainedAccountData?: {
    securities_map: Record<
      string,
      V2SecurityMetadata & { adjusted_allocation?: number }
    >;
    final_target: Record<string, string>;
    lot_adjustments?: LotAdjustment[];
    allocation_tree?: V2AllocationTreeNode[];
  };
  completionAccountDetail?: V2CompletionAccountDetail;
  softCompletionAccountDetail?: V2CompletionAccountDetail;
  lossHarvestModifications?: V2LossHarvestModification[];
  sharedData: SharedRebalanceData;
  rawManualRebalanceData?: any;
}

export const AccountExplainSection = ({
  accountId,
  v2TargetsData,
  constrainedAccountData,
  completionAccountDetail,
  softCompletionAccountDetail,
  lossHarvestModifications,
  sharedData,
  rawManualRebalanceData,
}: AccountExplainSectionProps) => {
  const { rebalance, household } = useContext(RebalanceContext);

  const rebalanceAccount = rebalance?.accounts?.find(
    (acc) => acc.id === parseInt(accountId),
  );

  const manualRebalanceData = rawManualRebalanceData
    ? extractManualRebalanceData(
        accountId,
        rawManualRebalanceData,
        sharedData,
        rebalance?.type,
      )
    : null;

  const accountName =
    sharedData.accounts[accountId]?.account_name || `Account ${accountId}`;

  return (
    <div className="mb-5">
      <Row>
        <Col md={12}>
          {/* RebalanceAccountInsights */}
          {manualRebalanceData ? (
            <div className="mb-4">
              <RebalanceAccountInsightsDataOnly {...manualRebalanceData} />
            </div>
          ) : hasRebalanceMetrics(rebalanceAccount) ? (
            <div className="mb-4">
              <RebalanceAccountInsights
                rebalanceAccount={
                  rebalanceAccount as RebalanceAccountWithMetrics
                }
                rebalanceType={rebalance?.type || "tolerance"}
                householdModelId={household?.assignedModelId}
                modelAssignmentStrategy={household?.modelAssignmentStrategy}
                showCalculatedTargetForResidual
              />
            </div>
          ) : null}

          {/* Collapsible Calculations Section with Target Allocations */}
          {(v2TargetsData ||
            constrainedAccountData ||
            completionAccountDetail ||
            softCompletionAccountDetail) && (
            <Accordion className="mb-4">
              <Accordion.Item
                eventKey="0"
                style={{
                  border: "none",
                }}
              >
                <Accordion.Header
                  as="div"
                  style={
                    {
                      "--bs-accordion-active-bg": "var(--bs-accordion-bg)",
                      "--bs-accordion-active-color":
                        "var(--bs-accordion-btn-color)",
                      "--bs-accordion-btn-padding-x": "0.5rem",
                      "--bs-accordion-btn-padding-y": "0.5rem",
                      border: "1px solid var(--bs-accordion-border-color)",
                      borderRadius: "var(--bs-accordion-border-radius)",
                    } as React.CSSProperties
                  }
                >
                  Calculations
                </Accordion.Header>
                <Accordion.Body style={{ padding: 0, border: "none" }}>
                  {/* Soft Completion Strategy (per model account) */}
                  {softCompletionAccountDetail && (
                    <SoftCompletionAccountSection
                      detail={softCompletionAccountDetail}
                    />
                  )}

                  {/* Completion Strategy (per residual account) */}
                  {completionAccountDetail && (
                    <CompletionAccountSection
                      detail={completionAccountDetail}
                    />
                  )}

                  {/* Target Allocation Table */}
                  {v2TargetsData && (
                    <div className="mb-4">
                      <h5>Target Allocations</h5>
                      <V2AccountTargetTable
                        accountTargets={v2TargetsData}
                        accountBalance={
                          sharedData.accounts[accountId]?.balance || 0
                        }
                        isTaxable={
                          sharedData.accounts[accountId]?.is_taxable === true ||
                          sharedData.accounts[accountId]?.settings?.tax_type ===
                            "taxable"
                        }
                        trades={sharedData.trades?.[accountId]}
                      />
                    </div>
                  )}

                  {/* Constrained Target Table */}
                  {constrainedAccountData && (
                    <AccountConstrainedTable
                      accountId={accountId}
                      accountData={constrainedAccountData}
                      accountName={accountName}
                    />
                  )}

                  {/* Allocation Tree (Risk → Asset Class → Securities) */}
                  {constrainedAccountData?.allocation_tree && (
                    <AllocationTreeView
                      tree={constrainedAccountData.allocation_tree}
                    />
                  )}

                  {/* Lot Adjustments Table (for taxable accounts with lot_adjustments data) */}
                  {constrainedAccountData?.lot_adjustments &&
                    constrainedAccountData.lot_adjustments.length > 0 &&
                    (sharedData.accounts[accountId]?.settings
                      ?.lot_selection_algorithm === "MTAX" ? (
                      <MtaxLotAdjustmentsTable
                        lotAdjustments={
                          constrainedAccountData.lot_adjustments as MtaxLotAdjustment[]
                        }
                      />
                    ) : (
                      <HifoLotAdjustmentsTable
                        lotAdjustments={
                          constrainedAccountData.lot_adjustments as HifoLotAdjustment[]
                        }
                      />
                    ))}

                  {/* Tax-Loss Harvest target modifications (APP-1205) */}
                  {lossHarvestModifications &&
                    lossHarvestModifications.length > 0 && (
                      <V2LossHarvestSection
                        modifications={lossHarvestModifications}
                      />
                    )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}
        </Col>
      </Row>
    </div>
  );
};

// Expandable allocation tree showing Risk → Asset Class → Securities
const AllocationTreeView = ({ tree }: { tree: V2AllocationTreeNode[] }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  if (!tree || tree.length === 0) return null;

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderNodes = (
    nodes: V2AllocationTreeNode[],
    depth: number,
    parentKey: string,
  ) =>
    nodes.map((node) => {
      const key = `${parentKey}/${node.name}`;
      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expanded.has(key);
      const allocated = parseFloat(node.allocated || node.constrained);
      const target = parseFloat(node.target);
      const delta = allocated - target;

      return (
        <React.Fragment key={key}>
          <tr
            style={{ cursor: hasChildren ? "pointer" : "default" }}
            onClick={() => hasChildren && toggle(key)}
            className={depth === 0 ? "fw-bold" : ""}
          >
            <td style={{ paddingLeft: `${depth * 1.2 + 0.25}rem` }}>
              {hasChildren && (
                <span className="me-1" style={{ fontSize: "0.7em" }}>
                  {isExpanded ? "▼" : "▶"}
                </span>
              )}
              {node.name}
            </td>
            <td className="text-end">
              {formatDollar(parseFloat(node.constrained))}
            </td>
            <td className="text-end">{formatDollar(target)}</td>
            <td className="text-end">{formatDollar(allocated)}</td>
            <td
              className={`text-end ${Math.abs(delta) > 0.01 ? (delta > 0 ? "text-success" : "text-danger") : ""}`}
            >
              {delta > 0 ? "+" : ""}
              {formatDollar(delta)}
            </td>
          </tr>
          {hasChildren &&
            isExpanded &&
            renderNodes(node.children!, depth + 1, key)}
        </React.Fragment>
      );
    });

  return (
    <div className="mb-3">
      <h5>Allocation Tree</h5>
      <Table size="sm" bordered style={{ fontSize: "0.85rem" }}>
        <thead className="table-light">
          <tr>
            <th>Level</th>
            <th className="text-end">Constrained</th>
            <th className="text-end">Target</th>
            <th className="text-end">Allocated</th>
            <th className="text-end">Delta</th>
          </tr>
        </thead>
        <tbody>{renderNodes(tree, 0, "")}</tbody>
      </Table>
    </div>
  );
};

interface RebalanceHouseholdExplainProps {
  // Optional: pass explain data directly (for manual rebalances)
  manualExplainData?: Array<{ step_name: string; explain_data: any }>;
  // Optional: raw manual rebalance response data (for extracting account metrics)
  rawManualRebalanceData?: any;
  // Manual rebalances have no persisted id, so the check panel has nothing to
  // query. Callers can opt out explicitly rather than relying on id=0 falsy-ness.
  showCheckPanel?: boolean;
}

const RebalanceHouseholdExplain = ({
  manualExplainData,
  rawManualRebalanceData,
  showCheckPanel = true,
}: RebalanceHouseholdExplainProps = {}) => {
  const { rebalance, household } = useContext(RebalanceContext);

  // Only fetch from API if no manual data is provided
  const shouldFetch = !manualExplainData;
  const {
    isPending,
    isError,
    data: fetchedExplainData,
  } = useAuthenticatedFetch<
    UnpackResponse<RebalancesController["getRebalanceExplain"]>
  >(
    `/rebalances/${rebalance?.id}/explain`,
    {},
    { enabled: shouldFetch && !!rebalance?.id },
  );

  if (shouldFetch && isPending) return <Loading />;
  if (shouldFetch && isError)
    return <div>Error loading rebalance explanation</div>;

  // Use manual data if provided, otherwise use fetched data
  const orderedSteps = manualExplainData
    ? manualExplainData
        .map((step, index) => ({
          explainId: index,
          stepName: step.step_name,
          explainData: step.explain_data,
          created: new Date(),
        }))
        .toSorted((a, b) => a.explainId - b.explainId)
    : fetchedExplainData?.data.toSorted((a, b) => a.explainId - b.explainId);

  // Create shared data using the extracted utility function
  const sharedData: SharedRebalanceData = createSharedRebalanceData(
    orderedSteps || [],
  );

  // Merge account names from rebalance context (since V2 explains no longer include account_name for PII reasons)
  if (rebalance?.accounts) {
    rebalance.accounts.forEach((rebalanceAccount) => {
      const accountId = String(rebalanceAccount.id);
      if (sharedData.accounts[accountId]) {
        sharedData.accounts[accountId].account_name =
          rebalanceAccount.accountName;
      }
    });
  }

  // For manual rebalances, merge account data from rawManualRebalanceData into sharedData
  if (rawManualRebalanceData) {
    Object.entries(rawManualRebalanceData).forEach(([key, value]) => {
      // Skip non-account keys like 'explains', 'total'
      if (
        key !== "explains" &&
        key !== "total" &&
        typeof value === "object" &&
        value !== null
      ) {
        if (sharedData.accounts[key]) {
          // Merge riskAllocation, assetAllocation, and model info from response
          sharedData.accounts[key].riskAllocation = (
            value as any
          ).riskAllocation;
          sharedData.accounts[key].balance = parseFloat(
            (value as any).balance || "0",
          );
          // Copy model_name and model_id from API response
          if ((value as any).model_name) {
            sharedData.accounts[key].model_name = (value as any).model_name;
          }
          if ((value as any).model_id) {
            sharedData.accounts[key].model_id = (value as any).model_id;
          }
        }
      }
    });
  }

  // Build account name map for completion strategy display
  const accountNameMap: Record<string, string> = {};
  if (rebalance?.accounts) {
    rebalance.accounts.forEach((acc) => {
      accountNameMap[String(acc.id)] = acc.accountName;
    });
  }

  // Build model name map from request step (account_id → model_name)
  const modelNameMap: Record<string, string> = {};
  const requestStepForModels = orderedSteps?.find(
    (step) => step.stepName === "request",
  );
  if (requestStepForModels?.explainData?.request?.accounts) {
    Object.entries(requestStepForModels.explainData.request.accounts).forEach(
      ([accountId, account]) => {
        const acc = account as { model_name?: string };
        if (acc.model_name) {
          modelNameMap[accountId] = acc.model_name;
        }
      },
    );
  }

  // V2: Find rebalance reason and hint from trigger step
  const triggerStep = orderedSteps?.find((step) => step.stepName === "trigger");
  const rebalanceReason = triggerStep?.explainData?.trigger?.reason;
  const rebalanceHint = triggerStep?.explainData?.trigger?.hint;
  // V2: Transform allocation_details from V2 format to expected Record<string, number>
  // V2 format: { Growth: { current_weight: "0.21", target_weight: "0.28", ... }, ... }
  // Expected format: { Growth: 0.21, Defensive: 0.79, ... }
  const v2AllocationDetails =
    triggerStep?.explainData?.trigger?.threshold_details?.allocation_details;
  const allocationDictionary = v2AllocationDetails
    ? Object.fromEntries(
        Object.entries(v2AllocationDetails).map(
          ([key, detail]: [string, any]) => {
            // Handle both object format (V2) and direct number/string format
            if (typeof detail === "object" && detail !== null) {
              return [key, parseFloat(detail.current_weight || "0")];
            }
            // Fallback: if detail is already a number or string
            return [key, parseFloat(String(detail) || "0")];
          },
        ),
      )
    : undefined;

  // V2: Extract step data for account-centric rendering
  const requestStep = orderedSteps?.find((step) => step.stepName === "request");
  const constrainedTargetStep = orderedSteps?.find(
    (step) => step.stepName === "constrained_target",
  );
  const targetsStep = orderedSteps?.find((step) => step.stepName === "targets");
  const adjustedTargetStep = orderedSteps?.find(
    (step) => step.stepName === "adjusted_target",
  );
  // Note: outcome step is handled in the switch statement below

  // V2: Extract residual models from completion_strategy step (for no-model completion accounts)
  const completionStrategyStep = orderedSteps?.find(
    (step) => step.stepName === "completion_strategy",
  );
  const residualModels = completionStrategyStep?.explainData
    ?.completion_strategy?.residual_models as
    Record<string, V2CompletionResidualModel> | undefined;

  // V2: Get all account IDs from request, constrained_target, or rawManualRebalanceData
  const accountIds = requestStep?.explainData?.request?.accounts
    ? Object.keys(requestStep.explainData.request.accounts)
    : constrainedTargetStep?.explainData?.constrained_target?.accounts
      ? Object.keys(
          constrainedTargetStep.explainData.constrained_target.accounts,
        )
      : rawManualRebalanceData
        ? Object.keys(rawManualRebalanceData).filter(
            (key) => key !== "explains" && key !== "total",
          )
        : [];

  // V2: Transform request step data to RebalanceRequestExplainStep format
  const transformedRequestData = requestStep?.explainData?.request
    ? {
        as_of_date: requestStep.explainData.request.as_of_date,
        household_model: {
          primary: Object.fromEntries(
            Object.entries(
              requestStep.explainData.request.household_model || {},
            ).map(([k, v]) => [k, parseFloat(v as string)]),
          ),
          secondary: {} as Record<
            string,
            Record<string, Record<string, unknown>>
          >,
        },
        accounts: Object.fromEntries(
          Object.entries(requestStep.explainData.request.accounts || {}).map(
            ([accountId, acc]: [string, any]) => {
              // Get account name from rebalance context (V2 explains no longer include account_name for PII reasons)
              const rebalanceAccount = rebalance?.accounts?.find(
                (a) => String(a.id) === accountId,
              );
              const accountName =
                rebalanceAccount?.accountName ||
                acc.account_name || // Fallback for legacy data
                `Account ${acc.account_id}`;
              return [
                accountId,
                {
                  account_id: acc.account_id,
                  bridge_account_id: 0,
                  account_name: accountName,
                  rebalance_type: "tolerance" as const,
                  is_rebalance_account: true,
                  is_taxable: acc.is_taxable,
                  raise_spend_cash_amount: null,
                  balance: acc.balance != null ? parseFloat(acc.balance) : null,
                  cash_balance:
                    acc.cash_balance != null
                      ? parseFloat(acc.cash_balance)
                      : null,
                  model_id: acc.model_id || 0,
                  model_name: acc.model_name,
                  model:
                    transformV2Model(acc.model) ??
                    (residualModels?.[accountId]
                      ? {
                          primary: Object.fromEntries(
                            Object.entries(
                              residualModels[accountId].residual_target_weights,
                            ).map(([k, v]) => [k, parseFloat(v as string)]),
                          ),
                          secondary: {},
                        }
                      : null),
                  security_restrictions: {},
                  cash_settings: {
                    target_cash_type: "absolute" as const,
                    target_cash_weight: null,
                    target_cash_dollar: null,
                    minimum_cash_trigger_weight: null,
                    minimum_cash_trigger_dollar: null,
                    minimum_cash_trigger_condition: "AND" as const,
                    maximum_cash_trigger_weight: null,
                    maximum_cash_trigger_dollar: null,
                    maximum_cash_trigger_condition: "AND" as const,
                  },
                  holdings: null,
                  settings: {
                    account_type: acc.account_type || "",
                    tax_type: (acc.is_taxable ? "taxable" : "tax_exempt") as
                      "taxable" | "tax_exempt",
                    gains_budget_lt: null,
                    gains_budget_st: null,
                    wash_sale_treatment: "avoid_wash_sales" as const,
                  },
                },
              ];
            },
          ),
        ) as Record<string, Account>,
      }
    : null;

  // V2: Transform constrained_target step to account data format
  const getConstrainedAccountData = (accountId: string) => {
    const v2Account =
      constrainedTargetStep?.explainData?.constrained_target?.accounts?.[
        accountId
      ];
    if (!v2Account || !v2Account.securities_map) return undefined;

    return {
      securities_map: v2Account.securities_map as Record<
        string,
        V2SecurityMetadata & { adjusted_allocation?: number }
      >,
      final_target: v2Account.final_target,
      lot_adjustments: v2Account.lot_adjustments,
      allocation_tree: v2Account.allocation_tree,
    };
  };

  // V2: Get targets data for an account from the targets step
  const getV2TargetsData = (accountId: string) => {
    const targetsData =
      targetsStep?.explainData?.targets?.by_account?.[accountId];
    if (!targetsData) return undefined;
    return {
      initial_holdings: targetsData.initial_holdings || {},
      model_target: targetsData.model_target || {},
      adjusted_target: targetsData.adjusted_target || {},
      constrained_target: targetsData.constrained_target || {},
      final_target: targetsData.final_target || {},
    };
  };

  // V2: Get per-account completion detail from the completion_strategy step (residual accounts only)
  const getCompletionAccountDetail = (accountId: string) => {
    const detail =
      completionStrategyStep?.explainData?.completion_strategy
        ?.account_details?.[accountId];
    if (!detail || detail.role === "model") return undefined;
    return detail;
  };

  // V2: Get per-account soft completion detail (model accounts with soft constraints)
  const isSoftCompletion =
    completionStrategyStep?.explainData?.completion_strategy?.strategy ===
    "soft_completion";
  const getSoftCompletionAccountDetail = (accountId: string) => {
    if (!isSoftCompletion) return undefined;
    const detail =
      completionStrategyStep?.explainData?.completion_strategy
        ?.account_details?.[accountId];
    if (!detail || detail.role !== "model" || !detail.original_model)
      return undefined;
    return detail;
  };

  // V2: Sum realized losses on tax-loss-harvest-flagged trades for the
  // household summary row (APP-1205). Yields 0 when no trades carry the flag
  // (including pre-feature payloads where the flag is always undefined); the
  // summary row is hidden in that case.
  const taxLossHarvestedTotal = Object.values(sharedData.trades || {}).reduce(
    (acc, accountTrades) => {
      Object.values(accountTrades).forEach((trade) => {
        if (trade.tax_loss_harvest_flag) {
          acc +=
            (trade.realized_gain_loss_st || 0) +
            (trade.realized_gain_loss_lt || 0);
        }
      });
      return acc;
    },
    0,
  );

  // V2: Per-account tax-loss-harvest target modifications (APP-1205).
  // The adjusted_target step's `target_modifications` is a flat household-level
  // list keyed by account_id; we filter per account for in-accordion rendering.
  const targetModifications = adjustedTargetStep?.explainData?.adjusted_target
    ?.target_modifications as V2LossHarvestModification[] | undefined;
  const getLossHarvestModifications = (accountId: string) =>
    targetModifications?.filter((m) => String(m.account_id) === accountId);

  // V2: targets step has household_summary but different format than V1 target_risk_allocations
  // V2 format provides growth/defensive weights at household level, not per-account risk allocations
  // The TargetRiskAllocationRow component expects V1 format with account_target_risk_allocations
  // For V2, we skip this section as the data structure is incompatible

  return (
    <div className="rebalance-explain" data-page="explain">
      {showCheckPanel && rebalance?.id ? (
        <RebalanceCheckPanel rebalanceId={rebalance.id} />
      ) : null}

      {/* V2: Request section (replaces inputs) */}
      {transformedRequestData && (
        <RebalanceRequestExplainStep
          key={requestStep?.explainId}
          explainData={transformedRequestData}
          rebalanceReason={rebalanceReason}
          rebalanceHint={rebalanceHint}
          allocationDictionary={allocationDictionary}
          triggerAllocationDetails={v2AllocationDetails}
          rebalanceType={rebalance?.type}
          householdModelId={household?.assignedModelId}
          modelAssignmentStrategy={
            household?.modelAssignmentStrategy ||
            requestStep?.explainData?.request?.model_assignment_strategy
          }
          householdBalance={household?.householdBalance}
        />
      )}

      {/* Render each account section */}
      {accountIds.length > 0 && <h4 className="mt-2">Accounts</h4>}
      {accountIds.length > 0 &&
        accountIds.map((accountId) => (
          <AccountExplainSection
            key={accountId}
            accountId={accountId}
            v2TargetsData={getV2TargetsData(accountId)}
            constrainedAccountData={getConstrainedAccountData(accountId)}
            completionAccountDetail={getCompletionAccountDetail(accountId)}
            softCompletionAccountDetail={getSoftCompletionAccountDetail(
              accountId,
            )}
            lossHarvestModifications={getLossHarvestModifications(accountId)}
            sharedData={sharedData}
            rawManualRebalanceData={rawManualRebalanceData}
          />
        ))}

      {/* V2: Remaining steps that aren't handled in account-centric view */}
      {orderedSteps?.map((step) => {
        switch (step.stepName) {
          // V2 steps already handled above
          case "request":
          case "trigger":
          case "adjusted_target":
          case "constrained_target":
          case "targets":
            return null;
          // V2 outcome step contains filterable metrics
          case "outcome":
            if (step.explainData?.outcome_v2?.filterable_metrics) {
              return (
                <FilterableAttributesStep
                  key={step.explainId}
                  explainData={step.explainData.outcome_v2.filterable_metrics}
                  taxLossHarvestedTotal={taxLossHarvestedTotal}
                />
              );
            }
            return null;
          // V2 completion strategy step — render soft or standard completion
          case "completion_strategy":
            if (step.explainData?.completion_strategy) {
              const strategyData = step.explainData.completion_strategy;
              if (strategyData.strategy === "soft_completion") {
                return (
                  <SoftCompletionExplain
                    key={step.explainId}
                    data={strategyData}
                    accountNames={accountNameMap}
                    modelNames={modelNameMap}
                  />
                );
              }
              return (
                <CompletionStrategyExplain
                  key={step.explainId}
                  data={strategyData}
                  accountNames={accountNameMap}
                  modelNames={modelNameMap}
                />
              );
            }
            return null;
          default:
            return null;
        }
      })}
    </div>
  );
};
export default RebalanceHouseholdExplain;
